import React, { useEffect, useState } from 'react';
import { MobileVerifyResponseType } from '../../../hooks/useMobileVerify';
import { JBAlert, JBButton, JBCodeInput, JBTypography } from '@jbhi-fi/lanyard-ui';
import { Props as AlertProps } from '@jbhi-fi/lanyard-ui/components/Alert/Alert';
import { useMobileVerifyContext } from '../../../context/MobileVerifyContext';
import { ALERTS, VALIDATION_ERRORS } from './constants';
import CustomerHelper from '../../../helpers/CustomerHelper';
import { useTrackingContext, trackingContext, formAction } from '@jbhi-fi/components/src/Tracking';
import { useVerifyLayout } from '../../../hooks/useVerifyLayout/useVerifyLayout';

const customerHelper = new CustomerHelper();

const VerifyCode = ({ handleRedirect }: { handleRedirect: (data: MobileVerifyResponseType) => void }): JSX.Element => {
  const [codeInputKey, setCodeInputKey] = useState(1);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState<boolean>(null);
  const [resending, setResending] = useState<boolean>(null);
  const [validationError, setValidationError] = useState<string>();
  const [alert, setAlertState] = useState<AlertProps>(null);
  const { data, setMobileVerifyData } = useMobileVerifyContext();
  const { themeContext } = useVerifyLayout();
  const { receiptCode } = themeContext ?? {};
  const { tracker } = useTrackingContext();

  const setAlert = (alert: AlertProps) => {
    setVerifying(null);
    setResending(null);
    setCodeInputKey(codeInputKey + 1);
    setAlertState(alert);
  };

  useEffect(() => {
    document.title = 'Verify mobile number - Account - JB Hi-Fi';
    tracker.group = 'enter otp';
    tracker.trackFormEvent({ action: formAction.stepView });
  }, []);

  useEffect(() => {
    setCode('');
  }, [codeInputKey]);

  useEffect(() => {
    if (resending || verifying) {
      setAlertState(null);
      setValidationError(undefined);
      tracker.trackClick({ label: resending ? 'resend' : 'verify' });
    }
  }, [resending, verifying]);

  useEffect(() => {
    if (validationError) {
      setVerifying(null);
      setResending(null);
    }
  }, [validationError]);

  useEffect(() => {
    if (alert?.type === 'error') {
      tracker.trackError({
        errorMsg: typeof alert.heading === 'string' ? alert.heading : 'Error',
        contextOverride: trackingContext.form,
      });
    }
  }, [alert]);

  useEffect(() => {
    if (code) {
      setValidationError(isCodeValid() ? null : VALIDATION_ERRORS.required);
    }
  }, [code]);

  const isCodeValid = (): boolean => {
    return code.length === 4;
  };

  const handleVerify = async (
    event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    tracker.trackFormEvent({ label: 'Verify', action: formAction.submit });
    if (!isCodeValid()) {
      tracker.trackError({ errorMsg: VALIDATION_ERRORS.required, contextOverride: trackingContext.form });
      return setValidationError(VALIDATION_ERRORS.required);
    }

    setVerifying(true);
    let response;
    try {
      response = await customerHelper.verifyCustomerMobile(data.customerId, code);
      setMobileVerifyData(response);
    } catch (error) {
      return setAlert(ALERTS.linkError);
    }
    if (response.isSuccess) {
      tracker.trackFormEvent({
        action: formAction.complete,
        extraData: receiptCode && { data: { value: receiptCode } },
      });
      setMobileVerifyData({ isFinished: true, isJustVerified: true });
      handleRedirect({ ...response, isFinished: true, isJustVerified: true });
    } else if (response.isTooManyAttempts) {
      handleRedirect(response);
    } else if (response.isNoMatch) {
      setCodeInputKey(codeInputKey + 1);
      setValidationError(VALIDATION_ERRORS.invalid);
      tracker.trackError({ errorMsg: VALIDATION_ERRORS.invalid, contextOverride: trackingContext.form });
    } else {
      setAlert(ALERTS.linkError);
    }
  };

  const handleResend = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    setResending(true);

    let response;
    try {
      response = receiptCode
        ? await customerHelper.sendCustomerMobileVerification(data.customerId, receiptCode)
        : await customerHelper.setCustomerMobile(data.customerId, data.mobileNumber, true);
      setMobileVerifyData(response);
    } catch (error) {
      setAlert(ALERTS.resendError);
    }

    if (response.isSuccess) {
      setAlert(ALERTS.resendSuccess);
    } else if (response.isTooManyAttempts) {
      handleRedirect(response);
    } else {
      setAlert(ALERTS.resendError);
    }
  };

  return (
    <>
      <div className="alerts">{alert ? <JBAlert {...alert} /> : null}</div>
      <div className="page-width verify-section">
        <JBTypography type="h2" margin="20px 0px 30px" align="center">
          Enter the verification
          <br />
          code we sent to
          <br />
          {data.mobileNumber}
        </JBTypography>
        <form onSubmit={verifying ? event => event.preventDefault() : handleVerify}>
          <div className="code-inputs">
            <JBCodeInput
              key={codeInputKey}
              name="code_input"
              autoFocus
              label="Enter 4-digit verification code"
              isRequired
              length={4}
              errorShow={Boolean(validationError)}
              errorMessage={validationError}
              onChange={event => !resending && !verifying && setCode(event.target.value)}
              inputMode="numeric"
              errorShowDelay={150}
            />
          </div>
          <JBTypography align="center" type="p" margin="5px 0 30px">
            <JBButton
              type="link"
              onClick={resending ? null : handleResend}
              label={resending ? 'Resending...' : 'Resend'}
            />
          </JBTypography>
          <JBButton
            type="primary"
            fullWidth={true}
            label={verifying ? 'Verifying...' : 'Verify'}
            onClick={verifying ? null : handleVerify}
            submit
          />
        </form>
      </div>
    </>
  );
};

export default VerifyCode;
